import * as React from 'react';
import type { SerializedError } from '@reduxjs/toolkit';
import type { IntlFormatters, IntlShape, MessageDescriptor } from 'react-intl';
import { defineMessages } from 'react-intl';
import type { ApiError, ErrorCodes } from '@foundershub/api-client';
import { TextLink } from '@foundershub/components';

export function getFieldError(
  error: ApiError | SerializedError | undefined,
  fieldName: string,
  intl: IntlShape
): string | undefined {
  if (error && 'data' in error) {
    // APIError, only return field matches
    const errors = error.data.errors;
    const code: ErrorCodes | undefined = errors.find(({ field }) => field === fieldName)?.code;
    const values = code && code in errorMessageValues ? errorMessageValues[code] : undefined;
    return code ? intl.formatMessage(errorMessages[code], values) : undefined;
  }

  return error ? intl.formatMessage(errorMessages.error) : undefined;
}

export function getValidationState(error: ApiError | SerializedError | undefined, fieldName: string) {
  if (error && 'data' in error) {
    const errors = error.data.errors;
    const code: ErrorCodes | undefined = errors.find(({ field }) => field === fieldName)?.code;
    return code ? 'error' : 'success';
  }
  return 'success';
}

export function getErrorMessages(
  error: ApiError | SerializedError | undefined,
  intl: IntlShape,
  fallback?: MessageDescriptor
): Array<string> {
  let nonFieldErrors: Array<SerializedError> | undefined;
  if (error && 'data' in error) {
    nonFieldErrors = error.data.errors.filter(({ field, code }) => !field && code);
  }

  return (nonFieldErrors?.length ? nonFieldErrors : [{ code: 'error' } as SerializedError]).map((error) => {
    const errorCode = (error.code && error.code in errorMessages ? error.code : 'error') as ErrorCodes;

    const errorMessage = errorCode === 'error' && fallback ? fallback : errorMessages[errorCode];
    const values = errorCode in errorMessageValues ? errorMessageValues[errorCode] : { message: error.message };

    return intl.formatMessage(errorMessage, values);
  });
}

export function getMessageBarErrorMessages(
  error: ApiError | SerializedError | undefined,
  intl: IntlShape,
  fallback?: MessageDescriptor,
  values?: Parameters<IntlFormatters['formatMessage']>[1]
) {
  if (error && 'data' in error) {
    const errors = error.data.errors;
    return errors.map((error) => {
      const errorCode = error.code && error.code in messageBarErrorMessages ? error.code : 'error';
      const errorMessage =
        errorCode === 'error' && fallback ? fallback : messageBarErrorMessages[errorCode] || errorMessages[errorCode];
      return intl.formatMessage(errorMessage, values);
    });
  }
  return error ? [intl.formatMessage(fallback || errorMessages.error)] : undefined;
}

export function getErrorCodes(error: ApiError | SerializedError | undefined): Array<string> {
  let nonFieldErrors: Array<SerializedError> | undefined;
  if (error && 'data' in error) {
    nonFieldErrors = error.data.errors.filter(({ field, code }) => !field && code);
  }
  return (nonFieldErrors?.length ? nonFieldErrors : [{ code: 'error' } as SerializedError]).map((error) => {
    const errorCode = (error.code && error.code in errorMessages ? error.code : 'error') as ErrorCodes;
    return errorCode;
  });
}

export const errorMessages: Record<ErrorCodes, MessageDescriptor> = defineMessages<ErrorCodes>({
  error: {
    defaultMessage: 'There was an error.',
    description: 'error message',
  },
  required: {
    defaultMessage: 'This field is required.',
    description: 'error message',
  },
  null: {
    defaultMessage: 'This field cannot be empty.',
    description: 'error message',
  },
  blank: {
    defaultMessage: 'This field cannot be blank.',
    description: 'error message',
  },
  invalid: {
    defaultMessage: 'This field is not valid.',
    description: 'error message',
  },
  invalid_url: {
    defaultMessage: 'Invalid URL please prefix your website with https:// and follow the standard URL format',
    description: 'error message',
  },
  max_length: {
    defaultMessage: 'This field content is too long.',
    description: 'error message',
  },
  min_length: {
    defaultMessage: 'This field content is too short.',
    description: 'error message',
  },
  max_value: {
    defaultMessage: 'This field is too large.',
    description: 'error message',
  },
  min_value: {
    defaultMessage: 'This field is too small.',
    description: 'error message',
  },
  empty: {
    defaultMessage: 'This field is required.',
    description: 'error message',
  },

  // Account
  'acc-exceed-max-social-impacts': {
    defaultMessage: 'Please select 5 or fewer.',
    description: 'error message',
  },
  'acc-error-redeeming-url': {
    defaultMessage: 'Cannot redeem this benefit at this time.',
    description: 'error message',
  },
  'acc-error-redeeming-azure': {
    defaultMessage: 'Cannot redeem Azure at this time.',
    description: 'error message',
  },
  'acc-existing-azure-registration': {
    defaultMessage:
      'Azure credits have already redeemed for this Microsoft account. Please link a different account or contact support.',
    description: 'error message',
  },
  'acc-exceed-max-product-categories': {
    defaultMessage: 'Please select 5 or fewer.',
    description: 'error message',
  },
  'acc-exceed-max-partners': {
    defaultMessage: 'Please select 5 or fewer.',
    description: 'error message',
  },
  'acc-postal-code-required': {
    defaultMessage: 'Please enter a postal code.',
    description: 'error message',
  },
  'acc-recently-incorp-requires-incorp': {
    defaultMessage: 'Please ensure you have marked your business as incorporated.',
    description: 'error message',
  },

  'benefit-activation-expired': {
    defaultMessage: 'Benefit activation has expired.',
    description: 'error message',
  },
  'benefit-not-redeemed': {
    defaultMessage: 'This benefit has not yet been redeemed. Please redeem this benefit first, then try again.',
    description: 'error message',
  },
  'airs-active-without-consumption-data': {
    defaultMessage:
      'Since you activated the account recently we are yet to find information on your credits. Please hold on while we check.',
    description: 'error message',
  },
  'no-azure-user': {
    defaultMessage: 'No Azure user set.',
    description: 'error message',
  },
  'acc-user-is-not-a-member': {
    defaultMessage: 'New admin must be a member of the account.',
    description: 'error message',
  },

  // Application
  'app-postal-code-required': {
    defaultMessage: 'Please enter a postal code.',
    description: 'error message',
  },
  'app-already-exists': {
    defaultMessage: 'Only one application is allowed at a time.',
    description: 'error message',
  },
  'app-description-min-length': {
    defaultMessage: 'Please provide more information in your description to continue.',
    description: 'error message',
  },
  'app-exceed-max-product-categories': {
    defaultMessage: 'Please select 5 or fewer.',
    description: 'error message',
  },
  'app-exceed-max-rejected': {
    defaultMessage: 'Too many applications for this account have already been denied.',
    description: 'error message',
  },
  'app-exceed-max-partners': {
    defaultMessage: 'Please select 5 or fewer.',
    description: 'error message',
  },
  'app-ovdf-required': {
    defaultMessage: 'An error occurred. Please refresh and try again.',
    description: 'error message',
  },
  'app-ovdf-failed': {
    defaultMessage:
      'We are unable to validate your information. Please contact us and reference message code: 715-123290 and transaction ID: {message}',
    description: 'error message',
  },
  'app-account-exists': {
    defaultMessage: 'Account already exists for this user.',
    description: 'error message',
  },
  'app-recently-rejected': {
    defaultMessage: 'Too many recently applied and denied applications.',
    description: 'error message',
  },
  'app-recently-incorp-requires-incorp': {
    defaultMessage: 'Please ensure you have marked your business as incorporated.',
    description: 'error message',
  },
  'app-referral-email-must-be-microsoft': {
    defaultMessage: 'Referrals must come from an @microsoft.com email address',
    description: '{Locked="@microsoft.com"}, error message',
  },
  'app-bv-duns-format-invalid': {
    defaultMessage: 'DUNS number has to be 9 numeric values',
    description: 'Error message for validation of DUNS number',
  },
  'app-exceed-max-social-impacts': {
    defaultMessage: 'Please select 5 or fewer.',
    description: 'error message',
  },
  'acc-bv-duns-format-invalid': {
    defaultMessage: 'DUNS number has to be 9 numeric values',
    description: 'Error message for validation of DUNS number',
  },
  // Invitation
  'invitation-invalid-email-or-code': {
    defaultMessage: 'The email entered doesn’t match our records.',
    description: 'error message',
  },
  'invitation-email-already-verified': {
    defaultMessage: 'The email address for this invitation has already been verified.',
    description: 'error message',
  },
  'invitation-only-teammember-may-resend': {
    defaultMessage: 'Only team members may resend invitations.',
    description: 'error message',
  },

  // Invite
  'invite-already-invited': {
    defaultMessage: 'An invite for this person was already requested.',
    description: 'error message',
  },
  'invite-invalid-user': {
    defaultMessage: 'Invalid user for this account.',
    description: 'error message',
  },
  'invite-max-invitations': {
    defaultMessage: 'There are too many outstanding invitations open for this account.',
    description: 'error message',
  },
  'invite-max-users': {
    defaultMessage: 'The user limit for this account has been reached.',
    description: 'error message',
  },
  'invite-duplicate-email': {
    defaultMessage: 'A user with this email already exists.',
    description: 'error message',
  },
  'invite-sync-error': {
    defaultMessage: 'There was an error. Please try again.',
    description: 'error message',
  },
  // LevelUp
  'levelup-already-requested': {
    defaultMessage: 'An upgrade for this account has already been requested.',
    description: 'error message',
  },
  'levelup-invalid-user': {
    defaultMessage: 'Invalid user for this account.',
    description: 'error message',
  },
  'levelup-past-benefit-activation-deadline': {
    defaultMessage: 'Account has passed the benefit activation deadline.',
    description: 'error messaage',
  },

  // Visual Studio
  'vs-not-available': {
    defaultMessage: 'This benefit is not available at this time.',
    description: 'error message',
  },
  'vs-invalid-user': {
    defaultMessage: 'Invalid user for this account.',
    description: 'error message',
  },
  'vs-existing-user': {
    defaultMessage: 'This user already has an account.',
    description: 'error message',
  },
  'vs-exceed-max-licenses': {
    defaultMessage: 'Too many licenses for this account.',
    description: 'error message',
  },
  'vs-delete-failure': {
    defaultMessage: 'Unable to revoke this license.',
    description: 'error message',
  },

  // Email verification
  'emailvalidatetoken-canceled': {
    defaultMessage: 'Validation was cancelled. Please try again.',
    description: 'email validation error message',
  },
  'emailvalidatetoken-error': {
    defaultMessage: 'There was an error validating the email address.',
    description: 'email validation error message',
  },
  'emailvalidatetoken-fail-code-expired': {
    defaultMessage:
      'This verification token has expired. Please go back and resubmit your email address to generate a new token.',
    description: 'email validation error message',
  },
  'emailvalidatetoken-fail-delivery-failed': {
    defaultMessage: 'There was an error validating the email address.',
    description: 'email validation error message',
  },
  'emailvalidatetoken-fail-max-match-attempts-reached': {
    defaultMessage: 'There was an error validating the email address.',
    description: 'email validation error message',
  },
  'emailvalidatetoken-mismatched': {
    defaultMessage: 'Invalid verification token.',
    description: 'email validation error message',
  },
  'emailverify-unavailable-email': {
    defaultMessage: 'There is already a user with that contact email. Please pick another email.',
    description: 'email validation error message',
  },
  'emailverify-error-requestid-missing': {
    defaultMessage: 'There was an error validating the email address.',
    description: 'email validation error message',
  },
  'emailverify-email-website-domain-mismatch': {
    defaultMessage: 'Email should match the domain or sub-domain that was previously verified in domain verification.',
    description: 'email validation error message',
  },
  'emailverify-user-not-in-account': {
    defaultMessage: 'User is not a member of specified Account.',
    description: 'email validation error message',
  },
  'emailvalidatetoken-ratelimited': {
    defaultMessage: 'Too many attempts. Please wait a few minutes and try again.',
    description: 'email validation error message',
  },
  'userdetail-email-unverified': {
    defaultMessage: 'This email address is unverified.',
    description: 'error message',
  },
  'userdetail-emailverify-record-missing': {
    defaultMessage: 'Missing email verification request.',
    description: 'error message',
  },

  // Team member management
  'acc-remove-admin': {
    defaultMessage: 'Cannot remove the admin from an account.',
    description: 'error message',
  },
  'acc-remove-self': {
    defaultMessage: 'Cannot remove yourself from an account.',
    description: 'error message',
  },

  // Vetting
  'bv-already-exists': {
    defaultMessage: 'Business Verification request already exists.',
    description: 'error message',
  },
  'bv-appeal-limit-reached': {
    defaultMessage:
      'You have reached the maximum number of verification attempts. Please <link>file a support ticket</link> so our team can assist you further.',
    description: 'error message',
  },
  'bv-not-in-failed-state': {
    defaultMessage: 'Business verification was not successful. Please refresh and try again.',
    description: 'error messsage',
  },
  'bv-appeal-already-exists': {
    defaultMessage: 'A Business Verification request with this information already exists.',
    description: 'error message',
  },
  'bv-onevet-error-creating-appeal': {
    defaultMessage: 'Appeal creation encountered an error. Please try again.',
    description: 'error messsage',
  },
  'dv-already-exists': {
    defaultMessage: 'Domain Verification request already exists.',
    description: 'error message',
  },
  'dv-missing-website': {
    defaultMessage: 'Domain Verification requires website be saved on company profile.',
    description: 'error message',
  },
  'dv-appeal-limit-reached': {
    defaultMessage:
      'You have reached the maximum number of verification attempts. Please <link>file a support ticket</link> so our team can assist you further.',
    description: 'error message',
  },
  'dv-ov-domain-invalid': {
    defaultMessage: 'Your domain could not be verified. Please check your domain or contact support.',
    description: 'error message',
  },
  'dv-not-in-failed-state': {
    defaultMessage: 'Domain verification was not successful. Please refresh and try again.',
    description: 'error messsage',
  },
  'dv-appeal-already-exists': {
    defaultMessage: 'A Domain verification request with this information already exists.',
    description: 'error message',
  },
  'dv-onevet-error-creating-appeal': {
    defaultMessage: 'Appeal creation encountered an error. Please try again.',
    description: 'error messsage',
  },

  // Expert network
  'user-not-an-expert': {
    defaultMessage: 'User is not an Expert',
    description: 'error message',
  },
  'cannot-request-founder': {
    defaultMessage: 'Only Experts can be sent a connection request',
    description: 'error message',
  },
  'accepted-requests-only': {
    defaultMessage: "You cannot give feedback for a request that hasn't been accepted",
    description: 'error message',
  },
  'cannot-create-accepted-request': {
    defaultMessage: 'You cannot create a request in an accepted status',
    description: 'error message',
  },

  // Support Cases
  'support-error-creating-case': {
    defaultMessage: 'There was an error creating your support case. Please try again.',
    description: 'error message',
  },
  'support-file-too-large': {
    defaultMessage: 'Upoaded file was too large. Please try again.',
    description: 'error message',
  },
  'support-disallowed-file-type': {
    defaultMessage: 'File type not allowed. Please try again.',
    description: 'error message',
  },
});

export const messageBarErrorMessages: Partial<Record<ErrorCodes, MessageDescriptor>> = defineMessages({
  'emailverify-email-website-domain-mismatch': {
    defaultMessage:
      'The email format doesn’t match our requirements. If you need more help <link>contact support</link>.',
    description: 'error message',
  },
  'emailvalidatetoken-mismatched': {
    defaultMessage: 'The code that you input is incorrect. Re-enter the code to try again.',
    description: 'error message',
  },
  'emailvalidatetoken-fail-code-expired': {
    defaultMessage: 'The code that you input has expired. Resend the code to try again.',
    description: 'email validation error message',
  },
});

const errorMessageValues: Partial<Record<ErrorCodes, Parameters<IntlFormatters['formatMessage']>[1]>> = {
  'dv-appeal-limit-reached': {
    link: function Link(content: string) {
      return (
        <TextLink
          openNewTab
          href="https://support.microsoft.com/supportrequestform/354fe60a-ba6d-92ad-208a-6a41387aa9d8"
        >
          {String(content)}
        </TextLink>
      );
    },
  },
  'bv-appeal-limit-reached': {
    link: function Link(content: string) {
      return (
        <TextLink
          openNewTab
          href="https://support.microsoft.com/supportrequestform/354fe60a-ba6d-92ad-208a-6a41387aa9d8"
        >
          {String(content)}
        </TextLink>
      );
    },
  },
};
